/**
 * Throws worked out off the page's thread: `simulate` and `rethrow` as in `physics.ts`, but
 * async, run in the physics worker (`simWorker.ts`). Same code, same numbers (the page's
 * `tuning()` goes with each job), so the dice land as they would have on the page. Where the
 * worker can't start or a job fails, the job runs on the page as before.
 *
 * `simulate` remembers its last few throws: your own throw is worked out the moment it leaves
 * your hand (`DiceTray`), while the roll is on its way to the server, so when the roll comes back
 * the throw is ready and plays at once.
 */
import type { DiceToss } from '@kadice/shared';
import type { Dome, Pose, Recording, TraySize } from './physics';
import { tuning, type DiceTuning } from './tuning';

interface SimArgs {
  toss: DiceToss;
  count: number;
  size: TraySize;
  seconds: number;
  start?: readonly Pose[];
  fixed?: readonly Pose[];
  /** Popple: the bubble (`physics.poppleLayout`); the dice stay under it. */
  dome?: Dome | null;
}

interface RethrowArgs {
  rec: Recording;
  which: readonly number[];
  toss: DiceToss;
  size: TraySize;
  seconds: number;
  start?: readonly Pose[];
  dome?: Dome | null;
}

type Job = ({ kind: 'simulate' } & SimArgs) | ({ kind: 'rethrow' } & RethrowArgs);
export type SimJob = Job & { id: number; tuning: DiceTuning };
export type SimReply =
  { id: number; rec: Recording; error?: undefined } | { id: number; rec?: undefined; error: string };

let worker: Worker | null | undefined;
let nextId = 1;
const waiting = new Map<number, { resolve: (r: Recording) => void; reject: (e: Error) => void }>();

/** The worker, started on first use (null: none here, jobs run on the page). */
function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker = new Worker(new URL('./simWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<SimReply>) => {
      const job = waiting.get(e.data.id);
      waiting.delete(e.data.id);
      if (!job) return;
      if (e.data.rec) job.resolve(e.data.rec);
      else job.reject(new Error(e.data.error));
    };
    // The worker broke (failed to load, crashed): everything waiting, and from now on, on the page.
    worker.onerror = () => {
      worker?.terminate();
      worker = null;
      waiting.forEach((job) => job.reject(new Error('physics worker failed')));
      waiting.clear();
    };
  } catch {
    worker = null;
  }
  return worker;
}

/** Run a job in the worker, or on the page if there's no worker or it fails. */
function run(job: Job): Promise<Recording> {
  // Loaded only when needed: the page's own copy of the physics comes with the engine.
  const onPage = async (): Promise<Recording> => {
    const physics = await import('./physics');
    return job.kind === 'simulate'
      ? physics.simulate(job.toss, job.count, job.size, job.seconds, job.start, job.fixed, [], job.dome)
      : physics.rethrow(job.rec, job.which, job.toss, job.size, job.seconds, job.start, job.dome);
  };
  const w = getWorker();
  if (!w) return onPage();
  const id = nextId++;
  const done = new Promise<Recording>((resolve, reject) => {
    waiting.set(id, { resolve, reject });
    const sent: SimJob = { ...job, id, tuning: { ...tuning() } };
    w.postMessage(sent);
  });
  return done.catch(onPage);
}

/** Throws already worked out (or being worked out), newest last. */
const recent: { key: string; rec: Promise<Recording> }[] = [];
const KEEP = 4;

/** `physics.simulate`, off the page; the same throw asked again (same values, same tuning) is not worked out twice. */
export function simulate(args: SimArgs): Promise<Recording> {
  const key = JSON.stringify([args, tuning()]);
  const hit = recent.find((r) => r.key === key);
  if (hit) return hit.rec;
  const rec = run({ kind: 'simulate', ...args });
  recent.push({ key, rec });
  if (recent.length > KEEP) recent.shift();
  // A failed throw isn't kept: asked again, it is tried again.
  rec.catch(() => {
    const i = recent.findIndex((r) => r.rec === rec);
    if (i >= 0) recent.splice(i, 1);
  });
  return rec;
}

/**
 * Start the worker and play one small throw out unseen, so its script is loaded and the physics
 * code is warm before the first real throw (otherwise that one lags after every reload).
 */
export function warmUp(): void {
  const size = { w: 7.2, h: 12 };
  void run({ kind: 'simulate', toss: { v: [0, 0, 6], w: [4, 3, 2], s: 1 }, count: 3, size, seconds: 2 }).catch(
    () => {},
  );
}

/** `physics.rethrow`, off the page. */
export const rethrow = (args: RethrowArgs): Promise<Recording> => run({ kind: 'rethrow', ...args });
